import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirectSalePriceQuote } from '@designdeck/shared';
import type { Product } from '@/api/products';
import { cn } from '@/lib/utils';
import { formatMoney } from '../format-money';

interface DirectSalePriceBlockProps {
  product: Product;
  /** The product carries a design: from then on only the quote counts. */
  hasDecoration: boolean;
  /** The pieces chosen among the sizes the colour offers. */
  pieces: number;
  /** The quote as the query holds it right now, if it holds one. */
  quote: DirectSalePriceQuote | undefined;
  /** The quote on hand belongs to the selection before this one. */
  quoteIsPlaceholder: boolean;
  quoteFailed: boolean;
  onShowDetails: () => void;
}

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * Price, unit-price note and the way to the price details. Every amount is
 * the quote's own — nothing is added up here, and nothing is fetched.
 */
export function DirectSalePriceBlock({
  product,
  hasDecoration,
  pieces,
  quote,
  quoteIsPlaceholder,
  quoteFailed,
  onShowDetails,
}: DirectSalePriceBlockProps) {
  const { t, i18n } = useTranslation();

  // While the next quote is on its way the last amount stays, dimmed: an
  // empty price on every plus would make the column jump.
  const lastQuote = useRef<{
    productId: string;
    quote: DirectSalePriceQuote;
  } | null>(null);
  if (quote) lastQuote.current = { productId: product.id, quote };
  const shownQuote =
    quote ??
    (!quoteFailed && lastQuote.current?.productId === product.id
      ? lastQuote.current.quote
      : undefined);
  const priceIsStale = !quote || quoteIsPlaceholder;

  const money = (amount: string | number) =>
    formatMoney(amount, shownQuote?.currency ?? 'EUR', i18n.language);
  const priceLabel = hasDecoration
    ? shownQuote
      ? money(shownQuote.total)
      : ''
    : product.recommendedSalesPrice != null
      ? t('directSale.product.priceFrom', {
          price: money(product.recommendedSalesPrice),
        })
      : '';

  return (
    <div className="flex min-w-0 flex-col items-start">
      <span
        className={cn(
          'font-display text-[26px] font-bold leading-tight text-ink',
          hasDecoration && priceIsStale && 'opacity-60'
        )}
      >
        {priceLabel}
      </span>
      {hasDecoration && shownQuote && pieces === 0 && (
        <span className="text-xs text-mute">
          {t('directSale.desktop.unitPriceNote')}
        </span>
      )}
      <button
        type="button"
        disabled={!quote}
        onClick={onShowDetails}
        className={cn(
          'min-h-9 text-left text-sm text-ink underline underline-offset-4',
          focusRing,
          'disabled:text-ink-3'
        )}
      >
        {t('directSale.checkout.priceDetails')}
      </button>
    </div>
  );
}
